// Axios-specific cancellation type, thrown when a request's signal is aborted. An axios major
// upgrade that renames or reshapes it has to be reflected in the catch below, or every
// superseded request starts dispatching an error - see tests/services/hardwareInventory.
import { CanceledError } from 'axios';
import {
	COMPONENT_TYPE_JOB_POLL,
	GET_FLEET_COMPONENTS_DEFAULT_QUERY_PARAMS
} from '../../constants/hardware-inventory';
import { getApiErrorMessage } from '../../helpers/apiError';
import * as hardwareInventoryService from '../../services/hardwareInventory';
import { describeJobOutcome, isJobSucceeded, pollJobUntilTerminal } from '../../services/jobs';
import { pushDangerNotification, pushSuccessfulNotification, pushWarningNotification } from '../notifications/actions';
import * as actionTypes from './actionTypes';
import { formValuesToApprovalPayload } from './mapper';

export const getFleetComponents = (queryParams = GET_FLEET_COMPONENTS_DEFAULT_QUERY_PARAMS) => {
	return (dispatch) => {
		dispatch({ type: actionTypes.START_FETCHING_FLEET_COMPONENTS });

		return hardwareInventoryService
			.getFleetComponents(queryParams)
			.then(({ data }) => {
				dispatch({
					type: actionTypes.GET_FLEET_COMPONENTS_SUCCESS,
					payload: {
						components: data?.components ?? [],
						pagination: data?.pagination ?? {}
					}
				});

				return data?.components ?? [];
			})
			.catch((error) => {
				/*
				 * The service aborts the previous request on every new one, so a cancellation is the
				 * expected outcome of typing quickly rather than a failure worth showing.
				 */
				if (error instanceof CanceledError) {
					return;
				}

				dispatch({
					type: actionTypes.GET_FLEET_COMPONENTS_ERROR,
					payload: getApiErrorMessage(error, 'The fleet inventory could not be loaded.')
				});
			});
	};
};

export const getComponentTypes = () => {
	return (dispatch) => {
		dispatch({ type: actionTypes.START_FETCHING_COMPONENT_TYPES });

		return hardwareInventoryService
			.getComponentTypes()
			.then(({ data }) => {
				dispatch({
					type: actionTypes.GET_COMPONENT_TYPES_SUCCESS,
					payload: { componentTypes: data?.componentTypes ?? [] }
				});

				return data?.componentTypes ?? [];
			})
			.catch((error) => {
				dispatch({
					type: actionTypes.GET_COMPONENT_TYPES_ERROR,
					payload: getApiErrorMessage(error, 'The firmware analytics could not be loaded.')
				});
			});
	};
};

const UNCONFIRMED_MESSAGE = 'The approval was submitted but is taking longer than expected to apply. Refresh the analytics in a moment to confirm.';

/*
 * `services/jobs` knows what a `skipped` or `suppressed` job means, not what the job was doing, so
 * the noun is supplied here.
 */
const UNAPPLIED_MESSAGE = 'The firmware version was not approved';

const isAbort = (error) => {
	return error instanceof CanceledError || error?.name === 'AbortError';
};

export const showComponentTypeApprovalModal = (componentType) => {
	return { type: actionTypes.SHOW_COMPONENT_TYPE_APPROVAL_MODAL, payload: componentType };
};

export const hideComponentTypeApprovalModal = () => {
	return { type: actionTypes.HIDE_COMPONENT_TYPE_APPROVAL_MODAL };
};

/*
 * How a run of the approval write ended, for a caller whose own state depends on it landing.
 * `failed` covers every branch in which no approval was recorded - a rejected request, a response
 * carrying no job, and a job that ended un-succeeded - because they differ only in what they say.
 */
export const APPROVAL_WRITE_OUTCOMES = {
	APPLIED: 'applied',
	UNCONFIRMED: 'unconfirmed',
	FAILED: 'failed',
	ABANDONED: 'abandoned'
};

const abandonApproval = (dispatch) => {
	dispatch({ type: actionTypes.SAVE_COMPONENT_TYPE_APPROVAL_ABORTED });

	return APPROVAL_WRITE_OUTCOMES.ABANDONED;
};

const failApproval = (dispatch, message) => {
	dispatch({ type: actionTypes.SAVE_COMPONENT_TYPE_APPROVAL_ERROR, payload: message });
	dispatch(pushDangerNotification(message));

	return APPROVAL_WRITE_OUTCOMES.FAILED;
};

/*
 * Records the version HITS endorses for one component type, then re-reads the analytics.
 *
 * Same five steps and the same three failure shapes as the override pipeline, and deliberately
 * written out here rather than shared with it: that one refreshes a per-appliance list and this one
 * refreshes the whole catalog, so the only common part would be the polling, which already lives in
 * `services/jobs`.
 *
 *   Request rejected      - nothing was accepted. The modal stays open with the typed version
 *                           intact so it can be corrected in place.
 *   Poll never concluded  - the write WAS accepted; only watching it finish failed. Calling that a
 *                           failure would be wrong, so the modal closes, the catalog is re-read and
 *                           the outcome is reported as unconfirmed.
 *   Job ended un-succeeded - `pollJobUntilTerminal` resolves on `failed`, `canceled`, `skipped` and
 *                           `suppressed` too. As with the override API this is a primary error path
 *                           rather than an edge case: the endpoint validates its schema
 *                           synchronously, so an id that is well-formed but unknown comes back as a
 *                           200 plus a failed job.
 *
 * Resolves rather than rejects on a handled failure: redux-form surfaces a rejection that is not a
 * SubmissionError as an unhandled rejection, and clears `submitting` either way.
 *
 * The refresh re-reads `GET /hardware/component-types`, which is served from
 * `fleet_component_firmware_analytics`. That table is rebuilt off the `HardwareComponentTypeApproved`
 * event rather than inside the job, so a succeeded job does not guarantee the new version is in the
 * next response - see the caller, which is what tells the operator so.
 */
export const approveComponentTypeFirmwareVersion = (componentTypeId, values, options = {}) => {
	return async (dispatch) => {
		const { approvedBy, signal } = options;
		let job;
		let jobId;

		dispatch({ type: actionTypes.START_SAVING_COMPONENT_TYPE_APPROVAL });

		try {
			const { data } = await hardwareInventoryService.approveComponentTypeFirmwareVersion(
				componentTypeId,
				formValuesToApprovalPayload(values, approvedBy)
			);

			jobId = data?.jobId;
		} catch (error) {
			if (isAbort(error)) {
				return abandonApproval(dispatch);
			}

			return failApproval(dispatch, getApiErrorMessage(error, 'The firmware version could not be approved.'));
		}

		if (!jobId) {
			return failApproval(dispatch, 'The request was accepted but returned no job to track.');
		}

		try {
			job = await pollJobUntilTerminal(jobId, { ...COMPONENT_TYPE_JOB_POLL, signal });
		} catch (error) {
			if (isAbort(error)) {
				return abandonApproval(dispatch);
			}

			dispatch({ type: actionTypes.SAVE_COMPONENT_TYPE_APPROVAL_ERROR, payload: UNCONFIRMED_MESSAGE });
			dispatch(pushWarningNotification(UNCONFIRMED_MESSAGE));
			dispatch(hideComponentTypeApprovalModal());
			await dispatch(getComponentTypes());

			return APPROVAL_WRITE_OUTCOMES.UNCONFIRMED;
		}

		if (!isJobSucceeded(job)) {
			return failApproval(dispatch, describeJobOutcome(job, {
				failureMessage: 'The firmware version could not be approved.',
				unappliedMessage: UNAPPLIED_MESSAGE
			}));
		}

		dispatch({ type: actionTypes.SAVE_COMPONENT_TYPE_APPROVAL_SUCCESS });
		dispatch(hideComponentTypeApprovalModal());
		dispatch(pushSuccessfulNotification('Approved firmware version recorded.'));

		/*
		 * Awaited inside the same promise rather than fired and forgotten, so the Approve button
		 * stays disabled until the table has actually been re-read.
		 */
		await dispatch(getComponentTypes());

		return APPROVAL_WRITE_OUTCOMES.APPLIED;
	};
};
